




















import {
    useI18n,
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    defineComponent,
    nextTick,
} from '@vue/composition-api';
import { SelectionSourceScreen } from '@superbet-group/web.types';
import { getConfig } from '@superbet-group/web.lib.config';

import Odd from '@core/models/offer/Odd';
import SuperBetsMarket from '@modules/eventGrid/components/markets/superBets/SuperBetsMarket.vue';
import { getMarkets } from '@modules/eventGrid/eventRowUtils';
import { useMatch } from '@modules/news/components/composeable/useMatch';
import { SportOfferDataStore } from '@store/modules/data/sportOffer';
import { BetslipStore } from '@store/modules/ui/betSlip';
import { sortSuperBets } from '@modules/eventGrid/components/markets/helpers';
import { SportOfferMarketsStore } from '@store/modules/ui/sportOffer/markets';
import { getSuperbetsGroupId } from '@modules/eventGrid/components/EventRow/marketGroupHelpers';
import { RouteName } from '@app/router/constants/RouteName';
import { logger } from '@app/logger';

export default defineComponent({
    name: 'SuperBetsWidget',
    props: {
        matchId: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        const sportOfferStore = useVuexStore<SportOfferDataStore>('data/sportOffer');
        const betslipStore = useVuexStore<BetslipStore>('ui/sportOffer/betSlip');
        const sportOfferMarketsModule = useVuexStore<SportOfferMarketsStore>('ui/sportOffer/markets');

        const { translate } = useI18n();
        const { router } = useVueRouter();

        const oddValueDynamics = sportOfferStore.getter('oddValueDynamics');
        const getMarketGroupInfosForSportId = sportOfferStore.getter('getMarketGroupInfosForSportId');
        const eventOddMap = betslipStore.getter('eventOddMap');
        const toggleSelection = betslipStore.action('toggleSelection');
        const setSelectedMarketGroupId = sportOfferMarketsModule.action('setSelectedMarketGroupId');

        const { match } = useMatch(props.matchId);

        const isEventLocked = computed(() => match.value?.isLocked ?? false);
        const isEventOngoing = computed(() => match.value?.isOngoing?.() ?? false);

        const selectedOdds = computed(() => {
            if (!match.value) {
                return new Set();
            }

            return eventOddMap.value[match.value.id] ?? new Set();
        });

        const superBetMarket = computed(() => {
            if (!match.value) {
                return undefined;
            }

            return getMarkets(
                match.value,
                translate('superBets.marketName'),
                '', // Empty string here means "include every market", since it checks for "includes()"
                () => false, // Not needed, we don't use favourite markets in here
            ).find((market) => market.isSuperbet);
        });

        const odds = computed(() => {
            if (!superBetMarket.value) {
                return [];
            }

            return sortSuperBets(
                superBetMarket.value.odds,
                getConfig().superbets.sortDirection,
            );
        });

        const superBetsGroupId = computed(() => {
            if (!match.value || !superBetMarket.value) {
                return;
            }

            const marketGroupInfo = getMarketGroupInfosForSportId.value(match.value.sportId);
            return getSuperbetsGroupId(marketGroupInfo, [superBetMarket.value]);
        });

        function toggleOddSelection(odd: Odd): void {
            if (!match.value || match.value.isLocked || odd.isLocked) {
                return;
            }

            const { id: oddId, specialBetValue, uuid } = odd;
            toggleSelection({
                eventId: match.value.id,
                oddId,
                uuid,
                specialBetValue,
                sourceScreen: SelectionSourceScreen.NewsArticleArticleBody,
            });
        }

        async function navigateToSuperbets() {
            if (!match.value || !superBetsGroupId.value) {
                return;
            }

            try {
                await router.value.push({
                    name: RouteName.Sport.EventRedirect,
                    params: {
                        id: match.value.id.toString(),
                    },
                });
                await nextTick();
                await setSelectedMarketGroupId(superBetsGroupId.value);
            } catch (error) {
                logger.error(new Error(`Failed to navigate to Superbets: ${error}`));
            }
        }

        return {
            odds,
            match,
            selectedOdds,
            isEventLocked,
            isEventOngoing,
            superBetMarket,
            oddValueDynamics,
            toggleOddSelection,
            navigateToSuperbets,
        };
    },
    components: {
        SuperBetsMarket,
    },
});
